/* @flow */
/**
 * Redux Toolkit slice for the line summary report search workflow.
 * Manages request criteria, selected fields and filters, report results, exports, and
 * paginated result fetching.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { initialLineSummaryFieldsState, lineSummaryReportFieldList } from '../../constants/staticData';
import lineSummaryReportFieldMapping from '../../constants/fieldMappings/lineSummaryReportFieldMapping';
import lineSummaryReportsHeader from '../../constants/lineSummaryReportsHeader';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import appConfig from '../../appConfig';
import {
  toggleSpinner, updateDialogStates, togglePaginationSpinner
} from '../dialogStates/dialogStatesSlice';
import {
  getFieldsList,
  getRequestedFilterValuesInLineItemResults,
  getSelectedFilterValues,
  getFiltersInResponseRequestBody,
  getSelectedFilterValuesFromOpMetrics,
  setFieldsData,
  getRequestedFilterValues,
  getLineItemSelectedFilterKeys,
  getLineItemModifiedSelectedFilters
} from './lineSummaryHelper';
import {
  getFilterValuesFromResponse, mapInputFieldsToRequest,
  mapRequestToInputFields,
  checkForColumnData,
  mapFiltersToRequest,
  sortedFilterData,
  setFieldItemVas,
  setValidFields
} from '../../helpers/dataHelpers';
import { showForBIS, showForTypePayload, showForGoodSupplier } from '../../helpers/envServer';
import {
  settingsActions
} from '../profileSettings/settingSlice';
import lineSummaryReportFieldProperties from '../../constants/fieldProperties/lineSummaryReportFieldProperties';
import {
  removeFieldShipToAddress,
  setFieldShipToAddress,
  setFieldMfgCode,
  removeFieldMfgCode
} from '../poSearch/poSearchHelper';

const name = 'lineSummaryReport';

function createInitialState() {
  return {
    // initial state of request reducer
    isSavedSearchModified: false,
    lineSummaryReportRequestBody: null,
    lineSummaryReportInputFieldData: { ...initialLineSummaryFieldsState },
    lineSummaryReportSelectedFields: [],
    lineSummaryReportBookmarkName: '',
    lineSummaryReportBookmarkId: '',
    requestError: false,
    // result reducer
    lineSummaryReportResultData: {},
    lineSummaryReportSearchId: '',
    lineSummaryReportTotalCount: 0,
    lineSummaryReportNextResults: {},
    lineSummaryReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    lineSummaryReportSearchPanelId: '',
    lineSummaryReportFilterId: 'none',
    filterDropdownValues: {},
    selectedFilters: {
      [lineSummaryReportFieldMapping.LINE_SUMMARY_FX_ADJ_REQUIRED_INDICATOR]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_PLANT_CODE]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_FOB_MISMATCH_INDICATOR]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_SIZE_MISMATCH_ID]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_CUSTOMER_REQ_DATE]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_R3_DOC_TYPE]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_ITEM_STATUS]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_PO_REJECTION_CODE]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_CREATED_BY]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_TRANSPORT_MODE]: null,
      [lineSummaryReportFieldMapping.LINE_SUMMARY_GRC_USECASE_CODE]: null,
      ...(showForBIS && {
        [lineSummaryReportFieldMapping.LINE_SUMMARY_CONSUMER_CONSTRUCT_DIMENSION_NAME]: null
      }),
      ...(showForBIS && {
        [lineSummaryReportFieldMapping.LINE_SUMMARY_BIS_CLASSIFICATION]: null
      }),
      ...(showForGoodSupplier && {
        [lineSummaryReportFieldMapping.LINE_SUMMARY_GOODS_SUPPLIER_CODE]: null
      })
    },
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false,
    isLISRReportFilterBarOpen: false
  };
}
export const initialState = createInitialState();

/**
 * Reducer that stores the active line summary request body and flags saved-search changes.
 */
function updateLineSummaryReportRequestBody(state: Object, action: Object) {
  // Compare serialized request bodies to detect edits to an existing saved search.
  const requestExist = JSON.stringify(state.lineSummaryReportRequestBody);
  return {
    ...state,
    lineSummaryReportRequestBody: action.payload,
    isSavedSearchModified: Boolean(
      state.lineSummaryReportBookmarkId && requestExist !== JSON.stringify(action.payload)
    )
  };
}

/**
 * Reducer that explicitly updates whether the saved line summary search has been modified.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Reducer that stores a request body update without changing saved-search modification state.
 */
function updateLineSummaryReportRequestBodyOnSameBookmark(state: Object, action: Object) {
  return {
    ...state,
    lineSummaryReportRequestBody: action.payload
  };
}

/**
 * Reducer that stores selected line summary search field keys.
 */
function updateLineSummaryReportFields(state: Object, action: Object) {
  return {
    ...state, lineSummaryReportSelectedFields: action.payload
  };
}

/**
 * Reducer that stores line summary search input field values.
 */
function updateLineSummaryReportInputValues(state: Object, action: Object) {
  return {
    ...state, lineSummaryReportInputFieldData: action.payload
  };
}

/**
 * Reducer that updates the offset on the active line summary request body.
 */
function updateLineSummaryOffsetInRequest(state: Object, action: Object) {
  // Keep all search criteria intact while moving to a different API offset.
  const updatedBody = { ...state.lineSummaryReportRequestBody, offset: action.payload };
  return { ...state, lineSummaryReportRequestBody: updatedBody };
}

/**
 * Reducer that resets line summary input fields to their initial values.
 */
function resetLineSummaryReportInputData(state: Object) {
  return {
    ...state,
    lineSummaryReportInputFieldData: { ...initialLineSummaryFieldsState }
  };
}

/**
 * Reducer that stores the current saved search id and clears the modified flag.
 */
function updateCollabBookmarkId(state: Object, action: Object) {
  return {
    ...state, isSavedSearchModified: false, lineSummaryReportBookmarkId: action.payload
  };
}

/**
 * Reducer that stores the current saved search name and clears the modified flag.
 */
function updateCollabBookmarkName(state: Object, action: Object) {
  return {
    ...state, isSavedSearchModified: false, lineSummaryReportBookmarkName: action.payload
  };
}

/**
 * Reducer that clears line summary request, bookmark, selected field, and error state.
 */
function resetLineSummaryReportRequest(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    lineSummaryReportRequestBody: null,
    lineSummaryReportInputFieldData: { ...initialLineSummaryFieldsState },
    lineSummaryReportSelectedFields: [],
    lineSummaryReportBookmarkName: '',
    lineSummaryReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Reducer that updates filters on the active request and marks saved searches as modified.
 */
function updateLineItemReportFiltersRequest(state: Object, action: Object) {
  const updatedBody = { ...state.lineSummaryReportRequestBody, filter: action.payload };
  return {
    ...state,
    lineSummaryReportRequestBody: updatedBody,
    isSavedSearchModified: Boolean(state.lineSummaryReportBookmarkName)
  };
}

/**
 * Reducer that clears saved search metadata from line summary state.
 */
function clearSavedLineSummaryReport(state: Object) {
  return {
    ...state,
    isSavedSearchModified: false,
    lineSummaryReportBookmarkId: '',
    lineSummaryReportBookmarkName: ''
  };
}

/**
 * Reducer that clears the active line summary request body.
 */
function resetLineSummaryRequestForClearSearch(state: Object) {
  return {
    ...state,
    lineSummaryReportRequestBody: null
  };
}

/**
 * Reducer that replaces results with offset-zero data and resets cached next/previous pages.
 */
function lineSummaryReportOffset(state: Object, action: Object) {
  return {
    ...state,
    lineSummaryReportResultData: action?.payload,
    lineSummaryReportTotalCount: action?.payload?.pages?.totalResources,
    selectedRecordsCount: 0,
    // Clear cached adjacent result sets because this response becomes the active page.
    lineSummaryReportNextResults: null,
    lineSummaryReportPrevResults: null
  };
}

/**
 * Reducer that stores the current line summary page number.
 */
function updateLineSummaryPage(state: Object, action: Object) {
  return {
    ...state,
    page: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that stores the current line summary rows-per-page setting.
 */
function updateLineSummaryRowsPerPage(state: Object, action: Object) {
  return {
    ...state,
    rowsPerPage: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Reducer that stores line summary result data, total count, and a fresh search id.
 */
function updateLineSummaryReportResults(state: Object, action: Object) {
  return {
    ...state,
    lineSummaryReportResultData: action.payload,
    lineSummaryReportSearchId: cryptoRand().toString().substr(2, 8),
    lineSummaryReportTotalCount: action?.payload?.pages?.totalResources,
    selectedRecordsCount: 0,
    lineSummaryReportNextResults: null,
    lineSummaryReportPrevResults: null
  };
}

/**
 * Reducer that refreshes the line summary search id used to notify the UI.
 */
function updateLineSummaryReportSearchId(state: Object) {
  return {
    ...state, lineSummaryReportSearchId: cryptoRand().toString().substring(2, 8)
  };
}

/**
 * Reducer that refreshes the search panel id after result resets.
 */
function resetLineSummarySearchPanelId(state: Object) {
  return {
    ...state, lineSummaryReportSearchPanelId: cryptoRand().toString().substring(2, 8)
  };
}

/**
 * Reducer that stores fetched next-page data while keeping the current result set visible.
 */
function updateLineSummaryReportNextResult(state: Object, action: Object) {
  const payload = {
    // Flatten both current and fetched result sets before caching pagination data.
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    lineSummaryReportResultData: payload?.data,
    lineSummaryReportNextResults: payload?.dataNext,
    lineSummaryReportTotalPages: payload?.data?.pages?.totalPages,
    lineSummaryReportTotalCount: payload?.data?.pages?.totalResources
  };
}

/**
 * Reducer that moves cached next-page data into the current line summary results.
 */
function appendNextResultSet(state: Object) {
  const payload = {
    dataPrev: state.lineSummaryReportResultData,
    data: state.lineSummaryReportNextResults
  };
  return {
    ...state,
    lineSummaryReportResultData: payload.data,
    lineSummaryReportPrevResults: payload.dataPrev,
    lineSummaryReportNextResults: null
  };
}

/**
 * Reducer that appends next-page jump results and sets follow-up pagination fetch flags.
 */
function appendJumpToPageNextResultSet(state: Object, action: Object) {
  const payload = {
    dataPrev: state.lineSummaryReportResultData,
    data: state.lineSummaryReportNextResults
  };

  // Page group boundaries determine when another server fetch is needed after a jump.
  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  // The API returns 1000 rows at a time, so the boundary changes with rows per page.
  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    lineSummaryReportResultData: payload.data,
    lineSummaryReportPrevResults: payload.dataPrev,
    lineSummaryReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that stores fetched previous-page data while keeping the current result set visible.
 */
function updateLineSummaryReportPrevResult(state: Object, action: Object) {
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };
  return {
    ...state,
    lineSummaryReportResultData: payload.data,
    lineSummaryReportPrevResults: payload.dataPrev,
    lineSummaryReportTotalPages: payload?.data?.pages?.totalPages,
    lineSummaryReportTotalCount: payload?.data?.pages?.totalResources
  };
}

/**
 * Reducer that moves cached previous-page data into the current line summary results.
 */
function appendPrevResultSet(state: Object) {
  const payload = {
    data: state.lineSummaryReportPrevResults,
    dataNext: state.lineSummaryReportResultData
  };
  return {
    ...state,
    lineSummaryReportResultData: payload.data,
    lineSummaryReportNextResults: payload.dataNext,
    lineSummaryReportPrevResults: null
  };
}

/**
 * Reducer that appends previous-page jump results and sets follow-up pagination fetch flags.
 */
function appendJumpToPagePrevResultSet(state: Object, action: Object) {
  const payload = {
    data: state.lineSummaryReportPrevResults,
    dataNext: state.lineSummaryReportResultData
  };

  const numberString = state.page.toString();

  const rowPerPage = action.payload;

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString[numberString.length - 1] === '9';
    checkApiCallPrepend = numberString[numberString.length - 1] === '0';
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return {
    ...state,
    lineSummaryReportResultData: payload.data,
    lineSummaryReportNextResults: payload.dataNext,
    lineSummaryReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Reducer that resets line summary results, pagination, and cached page state.
 */
function resetLineSummaryReportResults(state: Object) {
  return {
    ...state,
    lineSummaryReportResultData: {},
    lineSummaryReportSearchId: '',
    lineSummaryReportTotalCount: 0,
    lineSummaryReportNextResults: {},
    lineSummaryReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,
    lineSummaryReportSearchPanelId: ''
  };
}

/**
 * Reducer that stores the line summary continuation token.
 */
function updateLineSummaryContinuationToken(state: Object, action: Object) {
  return {
    ...state,
    continuationToken: action.payload
  };
}

/**
 * Reducer that refreshes the line summary filter id used to notify the UI.
 */
function setLineSummaryReportFilterId(state: Object) {
  return {
    ...state,
    lineSummaryReportFilterId: cryptoRand().toString().substr(2, 8)
  };
}

/**
 * Reducer that updates selected values for one line item report filter.
 */
function bulkUpdateLineItemReportFilterValues(state: Object, action: Object) {
  if (!action.payload.filter) return { ...state };
  const modifiedSelectedFilters = {
    ...state.selectedFilters,
    [action.payload.filter]: action.payload.values
  };
  return { ...state, selectedFilters: modifiedSelectedFilters };
}

/**
 * Reducer that enables or disables multiple line item report filters.
 */
function bulkToggleFilters(state: Object, action: Object) {
  const modifiedSelectedFilters = { ...state.selectedFilters };
  Object.keys(action.payload).forEach((filter) => {
    if (action.payload[filter]) {
      if (!modifiedSelectedFilters[filter]) modifiedSelectedFilters[filter] = [];
    } else modifiedSelectedFilters[filter] = null;
  });
  return { ...state, selectedFilter: modifiedSelectedFilters };
}

/**
 * Reducer that stores filtersInResponse on the line item report request body.
 */
function updateLineItemReportFiltersInResposne(state: Object, action: Object) {
  const updatedBody = { ...state.body, filtersInResponse: action.payload };
  return { ...state, body: updatedBody };
}

/**
 * Reducer that replaces or merges selected line item report filters.
 */
function bulkUpdateSelectedLineItemFilters(state: Object, action: Object) {
  if (Array.isArray(action.payload)) {
    const modifiedSelectedFilters = { ...state.selectedFilters };
    Object.keys(modifiedSelectedFilters).forEach((filter: string) => {
      if (action.payload.includes(filter)) {
        if (!modifiedSelectedFilters[filter]) modifiedSelectedFilters[filter] = [];
      } else {
        modifiedSelectedFilters[filter] = null;
      }
    });
    return { ...state, selectedFilters: modifiedSelectedFilters };
  }
  if ((typeof action.payload === 'object') && action.payload.selectedFilters
      && Object.keys(action.payload.selectedFilters).length > 0) {
    const modifiedSelectedFilters = {
      ...state.selectedFilters,
      ...action.payload.selectedFilters
    };
    return { ...state, selectedFilters: modifiedSelectedFilters };
  }
  return { ...state, selectedFilters: initialState.selectedFilters };
}

/**
 * Reducer that clears all selected line summary filters and dropdown values.
 */
function clearFilters(state: Object) {
  // Reset every filter key to null while preserving the known filter shape.
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    selectedFilters[filter] = null;
  });
  return {
    ...state,
    filterDropdownValues: {},
    selectedFilters
  };
}

/**
 * Reducer that clears selected filter values while preserving which filters are enabled.
 */
function resetLineSummaryReportSelectedFiltersToInitialState(state: Object) {
  const selectedFilters = {};
  Object.keys(state.selectedFilters).forEach((filter) => {
    if (state.selectedFilters[filter]) {
      selectedFilters[filter] = [];
    } else {
      selectedFilters[filter] = null;
    }
  });
  return {
    ...state,
    selectedFilters
  };
}

/**
 * Reducer that clears line summary filter dropdown values.
 */
function resetLineSummaryFilterDropdownValue(state: Object) {
  return {
    ...state,
    filterDropdownValues: {}
  };
}

/**
 * Reducer that stores sorted line summary filter dropdown values.
 */
function updateReportFilterDropDownValues(state: Object, action: Object) {
  // Ignore empty dropdown payloads so existing options are not overwritten.
  if (
    action.payload
    && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: sortedFilterData(action.payload)
    };
  }
  return { ...state };
}

/**
 * Reducer that clears cached next line summary results.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    lineSummaryReportNextResults: null
  };
}

/**
 * Reducer that clears cached previous line summary results.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    lineSummaryReportPrevResults: null
  };
}

/**
 * Reducer that stores the target jump page and marks jump pagination as triggered.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}

/**
 * Reducer that toggles the jump-to-page trigger flag.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Reducer that toggles whether another next-page fetch is needed after appending.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Reducer that toggles whether another previous-page fetch is needed before prepending.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Reducer that stores whether the line summary filter bar is open.
 */
function updateLISRReportIsFilterBarOpen(state: Object, action: Object) {
  return {
    ...state,
    isLISRReportFilterBarOpen: action.payload
  };
}

/**
 * Builds the reducer map consumed by the line summary report slice.
 */
function createReducers() {
  // request reducer
  // resultreducer
  return {
    updateLineSummaryReportRequestBody,
    updateIsSavedSearchModified,
    updateLineSummaryReportRequestBodyOnSameBookmark,
    updateLineSummaryReportFields,
    updateLineSummaryReportInputValues,
    updateLineSummaryOffsetInRequest,
    resetLineSummaryReportInputData,
    updateCollabBookmarkId,
    updateCollabBookmarkName,
    resetLineSummaryReportRequest,
    updateLineItemReportFiltersInResposne,
    updateLineItemReportFiltersRequest,
    clearSavedLineSummaryReport,
    resetLineSummaryRequestForClearSearch,
    lineSummaryReportOffset,
    updateLineSummaryPage,
    updateLineSummaryRowsPerPage,
    updateLineSummaryReportResults,
    updateLineSummaryReportSearchId,
    resetLineSummarySearchPanelId,
    updateLineSummaryReportNextResult,
    appendNextResultSet,
    updateLineSummaryReportPrevResult,
    appendPrevResultSet,
    resetLineSummaryReportResults,
    updateLineSummaryContinuationToken,
    setLineSummaryReportFilterId,
    bulkUpdateLineItemReportFilterValues,
    bulkToggleFilters,
    bulkUpdateSelectedLineItemFilters,
    clearFilters,
    resetLineSummaryReportSelectedFiltersToInitialState,
    resetLineSummaryFilterDropdownValue,
    updateReportFilterDropDownValues,
    appendJumpToPageNextResultSet,
    appendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull,
    updateLISRReportIsFilterBarOpen
  };
}

export const reducers = createReducers();

export const slice = createSlice({ name, initialState, reducers });

export const actions = { ...slice.actions };

/**
 * Async thunk that builds a line summary search request and calls lineSummaryReportSearch.
 */
function fetchPOReports() {
  return createAsyncThunk(
    `${name}/fetchPOReports`,
    async ({
      data, callback, requestBody = {},
      shouldRequestFilterValues = true, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));

      // Collect default and required fields before deciding which columns to request.
      const lineItemSecondaryFields = [];
      getFieldsList(lineItemSecondaryFields);

      const state = getState();
      const {
        selectedColumnOrderOptions,
        selectedFilterOptions: filterOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};

      // Start with the user profile column order and add required API fields when needed.
      const columnOrderOptions = [...selectedColumnOrderOptions];
      if (checkForColumnData(state, true)) {
        lineSummaryReportsHeader.forEach((headRow: Object) => {
          if (headRow.includeInRequest) {
            columnOrderOptions.push(headRow.primary);
            if (headRow.secondary && !columnOrderOptions.includes(headRow.secondary)) {
              columnOrderOptions.push(headRow.secondary);
            }
          }
        });
      }
      const fieldsForRequest = checkForColumnData(state, true)
        ? columnOrderOptions
        : lineItemSecondaryFields;
      let lineItemRequestData = {};
      if (data) {
        lineItemRequestData = mapInputFieldsToRequest(data, 'lineSummaryReport');
        // Track which input fields were converted into request search criteria.
        const appliedSearchField = new Set(lineItemRequestData
          .map((field) => field.fieldName));
        dispatch(actions.updateLineSummaryReportInputValues(data));
        dispatch(actions.updateLineSummaryReportFields(lineSummaryReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedSearchField.has(field))));
      } else {
        lineItemRequestData = state.lineSummaryReport.lineSummaryReportRequestBody.search;
      }
      // Preserve existing filters when callers pass request body overrides.
      const filterRequest = Object.keys(requestBody).length
        ? state.lineSummaryReport.lineSummaryReportRequestBody.filter
        : [];
      let requestedBody = (state.lineSummaryReport.lineSummaryReportRequestBody) ? {
        ...state.lineSummaryReport.lineSummaryReportRequestBody,
        search: lineItemRequestData,
        filter: filterRequest,
        offset: '0',
        ...requestBody
      } : {
        fields: fieldsForRequest,
        search: lineItemRequestData,
        filter: Object.keys(requestBody).length
          ? state.lineSummaryReport.lineSummaryReportRequestBody.filter
          : [],
        count: appConfig.SearchThresholdLimit,
        offset: '0',
        ...requestBody
      };
      // Remove inactive or unsupported fields before the request is sent.
      requestedBody = setValidFields(
        lineSummaryReportFieldProperties, requestedBody, [], inactiveFeatureFields
      );
      requestedBody = setFieldItemVas(requestedBody);
      requestedBody = setFieldShipToAddress(requestedBody);
      requestedBody = setFieldMfgCode(requestedBody);

      if (shouldRequestFilterValues && !filterOptions) {
        getRequestedFilterValuesInLineItemResults(requestedBody, state);
      } else {
        getSelectedFilterValues(requestedBody, state);
      }
      // Append required include-only fields without duplicating selected fields.
      const payload = {
        ...requestedBody,
        fields: [
          ...requestedBody.fields,
          ...appConfig.includeFieldsLineSummary.filter(
            (item) => !requestedBody.fields.includes(item)
          )
        ]
      };
      request({
        api: 'lineSummaryReportSearch',
        method: 'post',
        // Some deployments require an explicit line item type in the payload.
        data: showForTypePayload ? { ...payload, type: 'lineItem' } : payload,
        cancellable: true
      }, dispatch).then((response) => {
        requestedBody = removeFieldMfgCode(requestedBody);
        requestedBody = removeFieldShipToAddress(requestedBody);
        dispatch(actions.updateLineSummaryReportRequestBody(requestedBody));
        dispatch(toggleSpinner(false));
        callback(response.data);
        if (shouldRequestFilterValues) {
          dispatch(actions.updateReportFilterDropDownValues(
            getFilterValuesFromResponse(response.data.filters || [])
          ));
        }
        if (shouldRequestFilterValues && (filterOptions && filterOptions.length > 0)) {
          dispatch(actions.bulkUpdateSelectedLineItemFilters(filterOptions));
        }
        dispatch(actions.updateLineSummaryRowsPerPage(rowsPerPageOption));
        dispatch(actions.updateLineSummaryReportResults(getflattenResponse(response.data)));
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        callback(null, error);
      });
    }
  );
}

/**
 * Async thunk that exports line summary or metrics data through the large-file export APIs.
 */
function exportPOReport() {
  return createAsyncThunk(
    `${name}/exportPOReport`,
    async ({
      callback, filetype, chart, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(toggleSpinner(true));
      const searchRequestBody = {
        ...state.lineSummaryReport.lineSummaryReportRequestBody
      };
      let requestBody = {
        search: [...(searchRequestBody.search) || []],
        fields: [...(searchRequestBody.fields) || []].filter((field: string) => (
          !appConfig.excludedLineSummaryFieldsInExportedFile.includes(field)
        )),
        filter: [...(searchRequestBody.filter) || []],
        searchType: searchRequestBody.searchType,
        fileFormat: filetype,
        type: 'lineItem'
      };
      requestBody = setValidFields(
        lineSummaryReportFieldProperties, requestBody, [], inactiveFeatureFields
      );
      requestBody = setFieldShipToAddress(requestBody);
      requestBody = setFieldItemVas(requestBody);
      requestBody = setFieldMfgCode(requestBody);

      const graphSummary = {};
      if (chart) {
        graphSummary.graphName = chart;
        // Attach chart data when exporting from an operational metrics visualization.
        graphSummary.data = state.OperationalMetrics[chart];// need to check after implementation
      }
      const chartRequestBody = {
        searchCriteria: {
          search: requestBody.search,
          fields: requestBody.fields,
          filter: requestBody.filter,
          searchType: requestBody.searchType,
          fileFormat: filetype
        },
        graphSummary
      };
      const requestApi = 'lineSummaryReportExportLargeFile';
      const requestMetricsApi = 'metricsExportLargeFile';
      request({
        api: chart ? requestMetricsApi : requestApi,
        method: 'post',
        data: chart ? chartRequestBody : requestBody
      }, dispatch).then((response) => {
        callback(response);
        dispatch(toggleSpinner(false));
      }).catch((error) => {
        callback(null, error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Finds the returned filter option that contains a saved filter value.
 */
function findAvailableFilterMatch(availableFilters, filterValue) {
  return availableFilters.find((availableFilter) => availableFilter.includes(filterValue));
}

/**
 * Maps saved filter values to available filter options returned by the API.
 */
function mapFilterValuesToAvailable(availableFilters, fieldValues) {
  return fieldValues
    .map((filterValue) => findAvailableFilterMatch(availableFilters, filterValue))
    .filter(Boolean);
}

/**
 * Builds selected filter state from saved search criteria and returned filter dropdown values.
 */
function buildSelectedFiltersFromCriteria(filters, filterValuesFromResponse) {
  const selectedFilters = {};
  if (!filters || filters.length === 0) {
    return selectedFilters;
  }
  filters.forEach((filter) => {
    const availableFilters = filterValuesFromResponse[filter.fieldName];
    selectedFilters[filter.fieldName] = mapFilterValuesToAvailable(
      availableFilters,
      filter.fieldValue
    );
  });
  return selectedFilters;
}

/**
 * Async thunk that executes a saved line summary report through lineSummaryReportSearch.
 */
function fetchSavedLineSummaryReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedLineSummaryReportResults`,
    async ({
      lineSummaryReportRequestData, callback, isOperationalCall, inactiveFeatureFields = []
    }, { getState, dispatch }) => {
      const requestBody = { ...lineSummaryReportRequestData.searchCriteria, offset: '0' };
      const state = getState();
      const {
        selectedFilterOptions: filterOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));

      const filterValuesOptByUser = getSelectedFilterValuesFromOpMetrics(filterOptions);

      const filtersInResponseData = getFiltersInResponseRequestBody(
        lineSummaryReportRequestData.searchCriteria.filter
      );
      // to include user default filters only for LISR
      if (filterOptions && filterOptions.length > 0 && !isOperationalCall) {
        filtersInResponseData.push(...filterValuesOptByUser);
      }
      // Deduplicate filtersInResponse by primary field before replaying a saved search.
      const filteredData = [...new Set(filtersInResponseData
        .map(({ primary }) => primary).filter(Boolean))]
        .map((primary) => filtersInResponseData.find(({ primary: p }) => p === primary));

      const savedSearchRequestBody = {
        ...requestBody,
        filtersInResponse: filteredData
      };

      let requestData = { ...savedSearchRequestBody };
      requestData = setValidFields(
        lineSummaryReportFieldProperties, requestData, [], inactiveFeatureFields
      );
      const fieldsData = [...requestData.fields];
      requestData = setFieldItemVas(requestData);
      // Operational saved-search calls need fields that are required but may not be persisted.
      setFieldsData(isOperationalCall, fieldsData);
      requestData = { ...requestData, fields: fieldsData };
      requestData = setFieldShipToAddress(requestData);
      requestData = setFieldMfgCode(requestData);
      const payloadModified = { ...requestData };
      // Pass the saved search id so the API can associate execution with the saved report.
      if (lineSummaryReportRequestData.id) {
        payloadModified.savedSearchID = lineSummaryReportRequestData.id;
      }
      request({
        api: 'lineSummaryReportSearch',
        method: 'post',
        cancellable: true,
        data: showForTypePayload ? { ...payloadModified, type: 'lineItem' } : payloadModified
      }, dispatch).then((response) => {
        requestData = removeFieldMfgCode(requestData);
        requestData = removeFieldShipToAddress(requestData);
        const fields = mapRequestToInputFields(
          [...lineSummaryReportRequestData.searchCriteria.search]
        );
        const lineSummaryReportSearchFields = new Set(
          lineSummaryReportRequestData.searchCriteria.search.map((field) => field.fieldName)
        );
        dispatch(actions.updateLineSummaryReportInputValues({
          ...fields
        }));
        dispatch(actions.updateLineSummaryReportRequestBody(requestData));
        dispatch(actions.updateLineSummaryReportFields(lineSummaryReportFieldList
          .map((field) => field.key)
          .filter((field) => lineSummaryReportSearchFields.has(field))));
        dispatch(actions.updateLineSummaryReportResults(getflattenResponse(response.data)));
        dispatch(actions.updateLineSummaryReportSearchId());
        dispatch(actions.updateLineSummaryPage(0));
        dispatch(actions.updateLineSummaryRowsPerPage(Number(rowsPerPageOption)));
        dispatch(actions.updateCollabBookmarkId(lineSummaryReportRequestData.id));
        dispatch(actions.updateCollabBookmarkName(lineSummaryReportRequestData.name));
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        dispatch(actions.clearFilters());
        const filterValuesFromResponse = getFilterValuesFromResponse(response.data.filters || []);
        dispatch(actions.updateReportFilterDropDownValues({
          ...state.lineSummaryReport.filterDropdownValues,
          ...filterValuesFromResponse
        }));
        // Restore selected filter values from saved criteria using available API options.
        const selectedFilters = buildSelectedFiltersFromCriteria(
          lineSummaryReportRequestData?.searchCriteria?.filter,
          filterValuesFromResponse
        );
        dispatch(actions.bulkUpdateSelectedLineItemFilters({ selectedFilters }));
        if (filterOptions.length > 0) {
          const updatedFilterValues = response?.data?.filters?.map((key) => key.filterName);
          dispatch(actions.bulkUpdateSelectedLineItemFilters(updatedFilterValues));
        }
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Async thunk that reloads line summary results from offset zero.
 */
function fetchLineSummaryreportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchLineSummaryreportsResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(toggleSpinner(true));
      if (
        state.lineSummaryReport.lineSummaryReportRequestBody
        && state.lineSummaryReport.lineSummaryReportResultData
      ) {
        let updatedRequest = state.lineSummaryReport.lineSummaryReportRequestBody;
        updatedRequest = setFieldItemVas(updatedRequest);
        updatedRequest = setFieldShipToAddress(updatedRequest);
        updatedRequest = setFieldMfgCode(updatedRequest);
        updatedRequest = {
          ...updatedRequest,
          offset: '0'
        };
        // Offset reloads do not need filter dropdown values in the response.
        delete updatedRequest.filtersInResponse;
        request({
          api: 'lineSummaryReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...updatedRequest, type: 'lineItem' } : updatedRequest
        }, dispatch).then((response) => {
          dispatch(actions.lineSummaryReportOffset(getflattenResponse(response.data)));
          dispatch(toggleSpinner(false));
          dispatch(actions.resetLineSummarySearchPanelId());
        }).catch((error) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
        });
      }
    }
  );
}

/**
 * Async thunk that dispatches the provided offset-reset fetch thunk.
 */
function lineSummaryReportDataOffset() {
  return createAsyncThunk(
    `${name}/lineSummaryReportDataOffset`,
    async (
      {
        linefetchLineSummaryreportsResultsOffset // todo on page
      }, { dispatch }
    ) => {
      dispatch(linefetchLineSummaryreportsResultsOffset());
    }
  );
}

/**
 * Async thunk that fetches the next page group for jump-to-page navigation.
 */
function fetchLineSummaryReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchLineSummaryReportJumpToPageNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      if (
        state.lineSummaryReport.lineSummaryReportRequestBody
        && state.lineSummaryReport.lineSummaryReportResultData?.pages?.next
      ) {
        const currentResultSet = state.lineSummaryReport.lineSummaryReportResultData;
        let updatedRequest = state.lineSummaryReport.lineSummaryReportRequestBody;
        // Use the next pagination link returned by the API as the source of the next offset.
        const searchResultNextOffset = state.lineSummaryReport
          .lineSummaryReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          // Extract the numeric offset from the API pagination link.
          offset: searchResultNextOffset.slice(7, resNext)
        };
        let requestBodyWithoutFilters = { ...updatedRequest };
        requestBodyWithoutFilters = setFieldItemVas(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldShipToAddress(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldMfgCode(requestBodyWithoutFilters);
        // Pagination requests reuse filters but skip refreshing dropdown values.
        delete requestBodyWithoutFilters.filtersInResponse;
        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'lineSummaryReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...requestBodyWithoutFilters, type: 'lineItem' } : requestBodyWithoutFilters
        }, dispatch).then((response) => {
          dispatch(actions.updateLineSummaryReportRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateLineSummaryReportNextResult({ response, currentResultSet }));
        }).catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          console.log('NEXT Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk that fetches the next line summary result set using stored pagination state.
 */
function fetchLineSummaryReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchLineSummaryReportNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (
        state.lineSummaryReport.lineSummaryReportRequestBody
        && state.lineSummaryReport.lineSummaryReportResultData?.pages?.next
      ) {
        const currentResultSet = state.lineSummaryReport.lineSummaryReportResultData;
        const currentJumpToPage = state.lineSummaryReport.jumpToPage || 0;
        // Jump pagination aligns UI pages to the API batch size of 1000 rows.
        const pageGroupSize = 1000 / state.lineSummaryReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.lineSummaryReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.lineSummaryReport.lineSummaryReportRequestBody;
        const searchResultNextOffset = state.lineSummaryReport
          .lineSummaryReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          // For jump navigation, request the first offset in the target API page group.
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };
        let requestBodyWithoutFilters = { ...updatedRequest };
        requestBodyWithoutFilters = setFieldItemVas(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldShipToAddress(requestBodyWithoutFilters);
        requestBodyWithoutFilters = setFieldMfgCode(requestBodyWithoutFilters);
        delete requestBodyWithoutFilters.filtersInResponse;
        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'lineSummaryReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...requestBodyWithoutFilters, type: 'lineItem' } : requestBodyWithoutFilters
        }, dispatch).then((response) => {
          dispatch(actions.updateLineSummaryReportRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateLineSummaryReportNextResult({ response, currentResultSet }));
          if (triggerJumpToPage) {
            dispatch(actions.appendJumpToPageNextResultSet(
              state.lineSummaryReport.rowsPerPage
            ));
            dispatch(actions.updatePreviousResultSettoNull());
          }
        }).catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
          console.log('NEXT Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk that dispatches the provided next-result fetch thunk.
 */
function reportsDataNextSet() {
  return createAsyncThunk(
    `${name}/reportsDataNextSet`,
    async (
      {
        linefetchLineSummaryReportNextResultSet
      }, { dispatch }
    ) => {
      dispatch(linefetchLineSummaryReportNextResultSet());
    }
  );
}

/**
 * Async thunk that fetches the previous page group for jump-to-page navigation.
 */
function fetchLineSummaryReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchLineSummaryReportJumpToPagePrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
      if (state.lineSummaryReport?.lineSummaryReportResultData?.pages?.prev) {
        const currentResultSet = state.lineSummaryReport.lineSummaryReportResultData;
        let updatedRequest = state.lineSummaryReport.lineSummaryReportRequestBody;
        // Use the previous pagination link returned by the API as the source of the
        // previous offset.
        const searchResultPrevOffset = state.lineSummaryReport.lineSummaryReportResultData
          .pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };
        let requestBody = { ...updatedRequest };
        requestBody = setFieldItemVas(requestBody);
        requestBody = setFieldShipToAddress(requestBody);
        requestBody = setFieldMfgCode(requestBody);
        // When filter values are not requested, avoid asking the API to return dropdown metadata.
        delete requestBody.filtersInResponse;

        request({
          api: 'lineSummaryReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...requestBody, type: 'lineItem' } : requestBody
        }, dispatch).then((response) => {
          dispatch(actions.updateLineSummaryReportRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateLineSummaryReportPrevResult({ response, currentResultSet }));
        }).catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          console.log('PREV Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk that fetches the previous line summary result set using stored pagination state.
 */
function fetchLineSummaryReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchLineSummaryReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
      if (state.lineSummaryReport?.lineSummaryReportResultData?.pages?.prev) {
        const currentResultSet = state.lineSummaryReport.lineSummaryReportResultData;
        const currentJumpToPage = state.lineSummaryReport.jumpToPage || 0;
        const pageGroupSize = 1000 / state.lineSummaryReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.lineSummaryReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.lineSummaryReport.lineSummaryReportRequestBody;
        const searchResultPrevOffset = state.lineSummaryReport.lineSummaryReportResultData
          .pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };
        let requestBody = { ...updatedRequest };
        requestBody = setFieldItemVas(requestBody);
        requestBody = setFieldShipToAddress(requestBody);
        requestBody = setFieldMfgCode(requestBody);
        delete requestBody.filtersInResponse;

        request({
          api: 'lineSummaryReportSearch',
          method: 'post',
          data: showForTypePayload ? { ...requestBody, type: 'lineItem' } : requestBody
        }, dispatch).then((response) => {
          dispatch(actions.updateLineSummaryReportRequestBodyOnSameBookmark(updatedRequest));
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateLineSummaryReportPrevResult({ response, currentResultSet }));
          if (triggerJumpToPage) {
            dispatch(actions.appendJumpToPagePrevResultSet(
              state.lineSummaryReport.rowsPerPage
            ));
            dispatch(actions.updateNextResultSettoNull());
          }
        }).catch((error) => {
          dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
          console.log('PREV Error ', error);
        });
      }
    }
  );
}

/**
 * Async thunk that dispatches the provided previous-result fetch thunk.
 */
function reportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/reportsDataPrevSet`,
    async (
      {
        linefetchLineSummaryReportPrevResultSet // todo on page
      }, { dispatch }
    ) => {
      dispatch(linefetchLineSummaryReportPrevResultSet());
    }
  );
}

/**
 * Async thunk that calls getGACReasonCode for line summary search support data.
 */
function getGacReasonCode() {
  return createAsyncThunk(
    `${name}/getGacReasonCode`,
    async ({
      callback
    }, { dispatch }) => {
      const payload = {};
      dispatch(toggleSpinner(true));
      request({
        api: 'getGACReasonCode',
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        payload.gacReasonCode = response.data;
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}

/**
 * Async thunk that applies a line summary filter and reruns lineSummaryReportSearch.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async ({
      payload,
      lineItemFilterCallback,
      shouldRequestLineItemFilterValues = false
    }, { getState, dispatch }) => {
      const state = getState();
      // Merge the changed filter into current selections before building API filter criteria.
      const selectedFilters = {
        ...state.lineSummaryReport.selectedFilters,
        [payload.filter]: payload.values
      };
      let requestBody = {
        ...state.lineSummaryReport.lineSummaryReportRequestBody,
        filter: mapFiltersToRequest(selectedFilters),
        offset: '0'
      };
      requestBody = setValidFields(lineSummaryReportFieldProperties, requestBody);
      // Only enabled filters can request refreshed dropdown values.
      const selectedFilterKeys = Object.keys(selectedFilters)
        .filter((filter) => selectedFilters[filter]);
      if (shouldRequestLineItemFilterValues && selectedFilterKeys.length > 0) {
        getRequestedFilterValues(requestBody, selectedFilterKeys);
      } else {
        // Skip response filter metadata unless refreshed filter values were requested.
        delete requestBody.filtersInResponse;
      }
      requestBody = setFieldShipToAddress(requestBody);
      requestBody = setFieldMfgCode(requestBody);

      dispatch(toggleSpinner(true));
      request({
        api: 'lineSummaryReportSearch',
        method: 'post',
        data: showForTypePayload ? { ...requestBody, type: 'lineItem' } : requestBody,
        cancellable: true
      }, dispatch).then((response) => {
        requestBody = removeFieldMfgCode(requestBody);
        requestBody = removeFieldShipToAddress(requestBody);
        dispatch(actions.updateLineSummaryReportRequestBody(requestBody));
        dispatch(toggleSpinner(false));
        const flattenedResponse = getflattenResponse(response.data);
        dispatch(actions.updateLineSummaryReportResults(flattenedResponse));
        dispatch(actions.updateLineSummaryPage(0));
        dispatch(actions.setLineSummaryReportFilterId());
        dispatch(actions.bulkUpdateLineItemReportFilterValues(payload));
        if (shouldRequestLineItemFilterValues && selectedFilterKeys.length > 0) {
          dispatch(actions.updateLineItemReportFiltersInResposne(requestBody.filtersInResponse));
        }
        if (response.data.filters) {
          dispatch(actions.updateReportFilterDropDownValues(
            getFilterValuesFromResponse(response.data.filters)
          ));
        }
        if (lineItemFilterCallback) {
          lineItemFilterCallback(response);
        }
      }).catch((error) => {
        if (lineItemFilterCallback) {
          lineItemFilterCallback(null, error);
        }
        console.log(error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Async thunk that clears filters and fetches unfiltered line summary search results.
 */
function fetchUnfilteredPOResults() {
  return createAsyncThunk(
    `${name}/fetchUnfilteredPOResults`,
    async (_, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      let requestBody = {
        ...state.lineSummaryReport.lineSummaryReportRequestBody,
        filter: [],
        offset: '0'
      };
      requestBody = setValidFields(lineSummaryReportFieldProperties, requestBody);
      requestBody = setFieldShipToAddress(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      request({
        api: 'lineSummaryReportSearch',
        method: 'post',
        data: showForTypePayload ? { ...requestBody, type: 'lineItem' } : requestBody,
        cancellable: true
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.clearFilters());
        dispatch(actions.resetLineSummarySearchPanelId());
        dispatch(actions.updateLineItemReportFiltersRequest([]));
        dispatch(actions.updateLineSummaryReportResults(getflattenResponse(response.data)));
      }).catch((error) => {
        console.log(error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Async thunk that coordinates follow-up dispatches after requesting new filter values.
 */
function aynscCallToDispatches() {
  return createAsyncThunk(
    `${name}/aynscCallToDispatches`,
    async (
      {
        response,
        filterDropdownValues,
        filtersInResponse,
        hasAppliedLineItemFiltersChanged,
        filtersToBeApplied,
        requestNewFiltersCallback
      },

      {
        dispatch
      }
    ) => {
      if (response.data.filters) {
        dispatch(actions.updateReportFilterDropDownValues({
          ...filterDropdownValues,
          ...getFilterValuesFromResponse(response.data.filters)
        }));
        dispatch(actions.updateLineItemReportFiltersInResposne(filtersInResponse));
      }
      if (hasAppliedLineItemFiltersChanged) {
        dispatch(actions.updateLineItemReportFiltersRequest(filtersToBeApplied));
        dispatch(actions.updateLineSummaryReportResults(getflattenResponse(response.data)));
        dispatch(actions.updateLineSummaryPage(0));
      }
      if (requestNewFiltersCallback) {
        requestNewFiltersCallback(response);
      }
    }
  );
}

/**
 * Async thunk that requests refreshed line summary filter values from lineSummaryReportSearch.
 */
function requestForNewFilters() {
  return createAsyncThunk(
    `${name}/requestForNewFilters`,
    async ({
      filtersSelected,
      enableCheckboxForDefault,
      requestNewFiltersCallback,
      TaynscCallToDispatches
    }, { getState, dispatch }) => {
      const state = getState();
      let requestBody = { ...state.lineSummaryReport.lineSummaryReportRequestBody };
      requestBody = setValidFields(lineSummaryReportFieldProperties, requestBody);
      requestBody = setFieldShipToAddress(requestBody);
      requestBody = setFieldMfgCode(requestBody);
      const {
        selectedFilters,
        filterDropdownValues
      } = state.lineSummaryReport;
      const modifiedSelectedFilters = { ...selectedFilters };
      // Mirror the checkbox list into selected filter state before calculating request changes.
      getLineItemModifiedSelectedFilters(modifiedSelectedFilters, filtersSelected);
      const lineItemFiltersToBeRequested = [];
      const filtersInResponse = [];
      let filtersToBeApplied = null;

      const selectedFilterKeys = Object.keys(modifiedSelectedFilters)
        .filter((filter) => modifiedSelectedFilters[filter]);
      // Only filters with selected values should remain in the API filter array.
      const appliedFiltersKeys = new Set(selectedFilterKeys.filter((filter) => (
        modifiedSelectedFilters[filter] && modifiedSelectedFilters[filter].length > 0
      )));
      // Detect when removing a filter requires rerunning the search without that filter.
      const hasAppliedLineItemFiltersChanged = (!(requestBody.filter || []).every((filter) => (
        appliedFiltersKeys.has(filter.fieldName)
      )));

      if (hasAppliedLineItemFiltersChanged) {
        filtersToBeApplied = mapFiltersToRequest(modifiedSelectedFilters);
        requestBody.filter = [...filtersToBeApplied];
      }
      getLineItemSelectedFilterKeys(
        selectedFilterKeys,
        modifiedSelectedFilters,
        filterDropdownValues,
        lineItemFiltersToBeRequested,
        filtersInResponse
      );
      // Skip filter metadata refresh when all selected filters already have dropdown values.
      if (lineItemFiltersToBeRequested.length === 0) {
        delete requestBody.filtersInResponse;
      } else {
        requestBody.filtersInResponse = lineItemFiltersToBeRequested;
      }
      requestBody.offset = '0';
      const selectedFilterData = {
        selectedFilterListForLineItemReport: filtersSelected
      };
      // Persist the selected filter list as the user default when requested.
      if (enableCheckboxForDefault) {
        dispatch(settingsActions.userProfileSettings({
          requestBody: selectedFilterData
        }));
      }
      dispatch(toggleSpinner(true));

      request({
        api: 'lineSummaryReportSearch',
        method: 'post',
        data: showForTypePayload ? { ...requestBody, type: 'lineItem' } : requestBody,
        cancellable: true
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.updateLineSummaryPage(0));
        dispatch(actions.setLineSummaryReportFilterId());
        dispatch(actions.resetLineSummarySearchPanelId());
        dispatch(actions.updateLineSummaryOffsetInRequest('0'));
        dispatch(actions.updateLineSummaryReportResults(getflattenResponse(response.data)));
        dispatch(actions.bulkUpdateSelectedLineItemFilters(filtersSelected));
        // Carry response-filter descriptors into the follow-up dispatch coordinator.
        dispatch(TaynscCallToDispatches({
          response,
          filterDropdownValues,
          filtersInResponse,
          hasAppliedLineItemFiltersChanged,
          filtersToBeApplied,
          requestNewFiltersCallback
        }));
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        if (requestNewFiltersCallback) {
          requestNewFiltersCallback(null, error);
        }
      });
    }
  );
}

/**
 * Creates async thunk action creators exposed by the line summary report slice.
 */
function createExtraActions() {
  return {
    fetchPOReports: fetchPOReports(),
    exportPOReport: exportPOReport(),
    fetchSavedLineSummaryReportResults: fetchSavedLineSummaryReportResults(),
    lineSummaryReportDataOffset: lineSummaryReportDataOffset(),
    fetchLineSummaryreportsResultsOffset: fetchLineSummaryreportsResultsOffset(),
    reportsDataNextSet: reportsDataNextSet(),
    fetchLineSummaryReportNextResultSet: fetchLineSummaryReportNextResultSet(),
    reportsDataPrevSet: reportsDataPrevSet(),
    fetchLineSummaryReportPrevResultSet: fetchLineSummaryReportPrevResultSet(),
    getGacReasonCode: getGacReasonCode(),
    applyFilters: applyFilters(),
    fetchUnfilteredPOResults: fetchUnfilteredPOResults(),
    requestForNewFilters: requestForNewFilters(),
    aynscCallToDispatches: aynscCallToDispatches(),
    fetchLineSummaryReportJumpToPageNextResultSet: fetchLineSummaryReportJumpToPageNextResultSet(),
    fetchLineSummaryReportJumpToPagePrevResultSet: fetchLineSummaryReportJumpToPagePrevResultSet()
  };
}

const extraActions = createExtraActions();

export const lineSummaryActions = { ...actions, ...extraActions };
export const lineSummaryReducer = slice.reducer;
